import React, { createContext, useState, useContext, ReactNode, useCallback, useMemo } from 'react';

// Define the shape of the context state
interface IModalContext {
    activeModal: string | null;
    openModal: (modalId: string) => void;
    closeModal: () => void;
}

// Create the context with a default value
const ModalContext = createContext<IModalContext | undefined>(undefined);

// Create a provider component
export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [activeModal, setActiveModal] = useState<string | null>(null);

    const openModal = useCallback((modalId: string) => setActiveModal(modalId), []);
    const closeModal = useCallback(() => setActiveModal(null), []);

    const value = useMemo(() => ({
        activeModal,
        openModal,
        closeModal,
    }), [activeModal, openModal, closeModal]);

    return (
        <ModalContext.Provider value={value}>
            {children}
        </ModalContext.Provider>
    );
};

// Create a custom hook to use the modal context
export const useModal = (): IModalContext => {
    const context = useContext(ModalContext);
    if (context === undefined) {
        throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
};